import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import PageHeader from '../../components/layout/PageHeader';
import Table from '../../components/ui/Table';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Textarea from '../../components/ui/Textarea';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import Modal from '../../components/ui/Modal';
import CandidateFilterBar, { useCandidateFilters } from '../../components/filters/CandidateFilterBar';
import PassportHistoryModal, { PASSPORT_STATUS } from '../../components/passport/PassportHistoryModal';
import SearchSummary, { withMatchColumn } from '../../components/ui/SearchMatch';
import { listPassportCandidates, createPassportRequest, listPassportReasons } from '../../api/passportRegister';
import { pushToast } from '../../components/ui/toastBus';

const dash = (v) => v || <span className="text-gray-400">—</span>;

function MovementModal({ target, onClose }) {
  const queryClient = useQueryClient();
  const { candidate, action } = target;
  const [reasonId, setReasonId] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState('');

  const reasonsQuery = useQuery({
    queryKey: ['passport-reasons', { action }],
    queryFn: () => listPassportReasons({ action }),
  });

  const mutation = useMutation({
    mutationFn: () => createPassportRequest({ candidate_id: candidate.id, action, passport_reason_id: Number(reasonId), note: note.trim() || undefined }),
    onSuccess: () => {
      pushToast({ type: 'success', message: `Passport ${action} request submitted.` });
      queryClient.invalidateQueries({ queryKey: ['passport-request-candidates'] });
      queryClient.invalidateQueries({ queryKey: ['passport-requests'] });
      queryClient.invalidateQueries({ queryKey: ['passport-history'] });
      onClose();
    },
    onError: (err) => {
      const errors = err.response?.data?.errors || {};
      setError(Object.values(errors)[0]?.[0] || err.response?.data?.message || 'Could not submit request.');
    },
  });

  const submit = () => {
    if (!reasonId) {
      setError('Select a reason.');
      return;
    }
    setError('');
    mutation.mutate();
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={action === 'collect' ? 'Collect passport' : 'Give passport'}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} loading={mutation.isPending}>Submit request</Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          {candidate.full_name} ({candidate.reference_no})
          {candidate.register_no ? ` · Register no ${candidate.register_no}` : ' · A register number is assigned on first approval.'}
        </p>
        <Select label="Reason" value={reasonId} onChange={(e) => setReasonId(e.target.value)}>
          <option value="">Select a reason…</option>
          {(reasonsQuery.data || []).map((r) => (
            <option key={r.id} value={r.id}>{r.name}</option>
          ))}
        </Select>
        <Textarea label="Note (optional)" rows={3} value={note} onChange={(e) => setNote(e.target.value)} />
        {error && <p className="text-sm text-red-600">{error}</p>}
      </div>
    </Modal>
  );
}

export default function PassportRegister() {
  const filters = useCandidateFilters();
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [target, setTarget] = useState(null);
  const [historyFor, setHistoryFor] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const candidatesQuery = useQuery({
    queryKey: ['passport-request-candidates', filters.params, search],
    queryFn: () => listPassportCandidates({ ...filters.params, search: search || undefined }),
  });
  const candidates = useMemo(() => (candidatesQuery.data || []).map((c, i) => ({ ...c, no: i + 1 })), [candidatesQuery.data]);

  const columns = [
    { key: 'no', label: 'No' },
    { key: 'register_no', label: 'Register No', render: (row) => (row.register_no ? <span className="font-semibold">{row.register_no}</span> : dash(null)) },
    { key: 'full_name', label: 'Name' },
    { key: 'vehicle_type', label: 'Vehicle Type', render: (row) => <span className="capitalize">{dash(row.vehicle_type)}</span> },
    { key: 'passport_no', label: 'Passport No', render: (row) => dash(row.passport_no) },
    { key: 'nationality', label: 'Nationality', render: (row) => dash(row.nationality) },
    { key: 'location', label: 'Location', render: (row) => <span className="capitalize">{dash(row.location)}</span> },
    { key: 'current_step', label: 'Current Workflow Stage', render: (row) => dash(row.current_step) },
    { key: 'contact_no', label: 'UAE Contact No', render: (row) => dash(row.contact_no) },
    {
      key: 'actions',
      label: '',
      render: (row) => {
        const info = PASSPORT_STATUS[row.passport_status];
        const held = row.passport_status === 'collected';
        return (
          <div className="flex items-center justify-end gap-2">
            <button
              onClick={() => setHistoryFor(row)}
              title={`Passport status: ${info.label}`}
              className={`rounded-md border px-2.5 py-1.5 text-xs font-medium ${info.button}`}
            >
              History · {info.label}
            </button>
            {row.has_pending_request ? (
              <Badge tone="amber">Requested</Badge>
            ) : (
              <Button onClick={() => setTarget({ candidate: row, action: held ? 'give' : 'collect' })}>
                {held ? 'Give' : 'Collect'}
              </Button>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div>
      <PageHeader
        title="Passport Request"
        description="Record passports collected from, and given back to, candidates. Each candidate keeps one register number for life."
      />

      <CandidateFilterBar {...filters.barProps}>
        <Input placeholder="Search name, reference, passport, contact…" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} className="w-72" />
      </CandidateFilterBar>

      <SearchSummary search={search} total={candidates.length} rows={candidates} plainCount={`${candidates.length} shown`} />
      <Table columns={withMatchColumn(columns, search)} data={candidates} isLoading={candidatesQuery.isLoading} emptyMessage="No candidates match this filter." />

      {target && <MovementModal target={target} onClose={() => setTarget(null)} />}
      <PassportHistoryModal candidate={historyFor} onClose={() => setHistoryFor(null)} />
    </div>
  );
}
